import { LiveAnnouncer } from '@angular/cdk/a11y';
import { GlobalPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, inject, Injectable, Injector, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import {
  NotificationDismissReason,
  NotificationPosition,
  NotificationTheme,
  NotificationType,
} from './models/notification.enum';
import {
  DEFAULT_NOTIFICATION_DEFAULTS,
  NOTIFICATION_DEFAULTS,
  NOTIFICATION_POSITION,
  NotificationConfig,
  NotificationDefaults,
  NotificationInstance,
  NotificationOptions,
} from './models/notification.model';
import { NotificationContainerComponent } from './notification-container/notification-container.component';
import { NotificationRef } from './notification-ref';

/** Severities that interrupt the user rather than waiting their turn. */
const ASSERTIVE_TYPES: ReadonlyArray<NotificationType> = [
  NotificationType.Error,
  NotificationType.Warning,
];

interface StackEntry {
  overlayRef: OverlayRef;
  componentRef: ComponentRef<NotificationContainerComponent>;
}

let uniqueId = 0;

/**
 * Logs every toast raised, and the stack that raised it.
 *
 * Toasts come from three dozen places — the HTTP interceptor, the no-data
 * notifier, and any component with something to say — and on screen they are
 * indistinguishable. When one appears that should not have, the only question
 * worth answering is which of those raised it, and `console.trace` answers it
 * exactly. Chrome files trace output under the Verbose level, so this costs a
 * reader nothing until they go looking for it.
 */
function traceToast(type: NotificationType, config: NotificationConfig): void {
  const summary = [config.title, config.message].filter(Boolean).join(' ');
  console.trace(`[toast] ${type}${config.key ? ` (key: ${config.key})` : ''}: ${summary}`);
}

/**
 * Raises toast notifications.
 *
 * ```ts
 * private readonly notify = inject(NotificationService);
 *
 * this.notify.success('Your account has been activated and you\'re signed in.', 'Account activated.');
 * this.notify.error('We couldn\'t connect to the server. Please refresh the page.', 'Connection blocked.', {
 *   duration: 0,
 *   action: notificationAction(NotificationActionPreset.Refresh, () => location.reload()),
 * });
 * ```
 *
 * Each screen position owns a CDK overlay that is created on first use and
 * then reused, so toasts at different positions never fight over the same
 * stack and leave animations always have a container to play out in.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  private readonly liveAnnouncer = inject(LiveAnnouncer);
  private readonly zone = inject(NgZone);
  private readonly router = inject(Router, { optional: true });

  private readonly stacks = new Map<NotificationPosition, StackEntry>();
  private readonly refs = new Map<string, NotificationRef>();
  private readonly destroyed = new Subject<void>();

  private notifications: NotificationInstance[] = [];
  private defaults: NotificationDefaults = {
    ...DEFAULT_NOTIFICATION_DEFAULTS,
    ...(inject(NOTIFICATION_DEFAULTS, { optional: true }) ?? {}),
  };
  private theme: NotificationTheme = this.defaults.theme;

  constructor() {
    this.router?.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroyed)
      )
      .subscribe(() => {
        // Read at emit time so `setDefaults()` can flip this after startup.
        if (this.defaults.dismissOnNavigation) {
          this.dismissAll();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
    this.stacks.forEach((stack) => stack.overlayRef.dispose());
    this.stacks.clear();
    this.refs.clear();
  }

  // ---- Public API ------------------------------------------------------

  success(message: string, title?: string, config?: NotificationOptions): NotificationRef {
    return this.open(NotificationType.Success, message, title, config);
  }

  error(message: string, title?: string, config?: NotificationOptions): NotificationRef {
    return this.open(NotificationType.Error, message, title, config);
  }

  warning(message: string, title?: string, config?: NotificationOptions): NotificationRef {
    return this.open(NotificationType.Warning, message, title, config);
  }

  info(message: string, title?: string, config?: NotificationOptions): NotificationRef {
    return this.open(NotificationType.Info, message, title, config);
  }

  /**
   * Fully configurable notification. Defaults to {@link NotificationType.Custom},
   * which uses the neutral palette and expects `icon` to be supplied.
   */
  custom(config: NotificationConfig & { type?: NotificationType }): NotificationRef {
    return this.show(config.type ?? NotificationType.Custom, config);
  }

  /** Raises a notification of an explicit type from a full config object. */
  show(type: NotificationType, config: NotificationConfig): NotificationRef {
    traceToast(type, config);

    return this.runInZone(() => {
      const existing = config.key ? this.findByKey(config.key) : undefined;
      const instance = this.resolve(type, config, existing?.id);

      if (existing) {
        // Same key already on screen: swap in the new content and let the toast
        // restart its timer instead of stacking a duplicate.
        this.notifications = this.notifications.map((n) => (n.id === existing.id ? instance : n));
        this.flush();
        this.announce(instance);
        return this.refs.get(existing.id)!;
      }

      const ref = new NotificationRef(instance.id, (id) =>
        this.dismiss(id, NotificationDismissReason.Programmatic)
      );
      this.refs.set(instance.id, ref);
      this.notifications = [...this.notifications, instance];
      this.enforceStackLimit(instance.position);
      this.flush();
      this.announce(instance);

      return ref;
    });
  }

  /** Closes one notification by id. Unknown ids are ignored. */
  dismiss(
    id: string,
    reason: NotificationDismissReason = NotificationDismissReason.Programmatic
  ): void {
    const next = this.notifications.filter((n) => n.id !== id);
    if (next.length === this.notifications.length) {
      return;
    }

    this.runInZone(() => {
      this.notifications = next;
      this.flush();

      const ref = this.refs.get(id);
      if (ref) {
        this.refs.delete(id);
        ref._settle(reason);
      }
    });
  }

  /** Closes every notification, or only those at `position` when given. */
  dismissAll(position?: NotificationPosition): void {
    this.notifications
      .filter((n) => !position || n.position === position)
      .forEach((n) => this.dismiss(n.id, NotificationDismissReason.Programmatic));
  }

  /** Switches the palette of every stack, current and future. */
  setTheme(theme: NotificationTheme): void {
    this.theme = theme;
    this.stacks.forEach((stack) => stack.componentRef.instance.setTheme(theme));
  }

  /** Merges new application-wide defaults over the current ones. */
  setDefaults(defaults: Partial<NotificationDefaults>): void {
    this.defaults = { ...this.defaults, ...defaults };
    if (defaults.theme) {
      this.setTheme(defaults.theme);
    }
  }

  // ---- Internals -------------------------------------------------------

  private open(
    type: NotificationType,
    message: string,
    title?: string,
    config?: NotificationOptions
  ): NotificationRef {
    return this.show(type, { ...config, message, title });
  }

  /** Merges a caller's config over the defaults into a renderable instance. */
  private resolve(
    type: NotificationType,
    config: NotificationConfig,
    reuseId?: string
  ): NotificationInstance {
    const assertive = ASSERTIVE_TYPES.indexOf(type) !== -1;

    return {
      ...config,
      id: reuseId ?? `du-toast-${++uniqueId}`,
      type,
      message: config.message,
      duration: config.duration ?? this.defaults.duration,
      position: config.position ?? this.defaults.position,
      dismissible: config.dismissible ?? this.defaults.dismissible,
      pauseOnHover: config.pauseOnHover ?? this.defaults.pauseOnHover,
      tapToDismiss: config.tapToDismiss ?? this.defaults.tapToDismiss,
      ariaLive: config.ariaLive ?? (assertive ? 'assertive' : 'polite'),
      role: config.role ?? (assertive ? 'alert' : 'status'),
    };
  }

  /**
   * Notifications are often raised from SignalR handlers, third-party callbacks
   * and `setTimeout`s that run outside Angular's zone, where nothing would
   * schedule the change-detection pass that renders them.
   */
  private runInZone<T>(fn: () => T): T {
    return NgZone.isInAngularZone() ? fn() : this.zone.run(fn);
  }

  private findByKey(key: string): NotificationInstance | undefined {
    return this.notifications.find((n) => n.key === key);
  }

  /** Evicts the oldest toasts once a position exceeds `maxStack`. */
  private enforceStackLimit(position: NotificationPosition): void {
    const atPosition = this.notifications.filter((n) => n.position === position);
    const overflow = atPosition.length - this.defaults.maxStack;

    for (let i = 0; i < overflow; i++) {
      this.dismiss(atPosition[i].id, NotificationDismissReason.Programmatic);
    }
  }

  /** Pushes the current state into every stack that has one. */
  private flush(): void {
    const positions = new Set<NotificationPosition>(this.notifications.map((n) => n.position));
    // Positions that just emptied still need a push so their last toast is
    // removed and its leave animation can play.
    this.stacks.forEach((_stack, position) => positions.add(position));

    positions.forEach((position) => {
      const stack = this.getStack(position);
      stack.componentRef.instance.setNotifications(
        this.notifications.filter((n) => n.position === position)
      );
    });
  }

  private announce(notification: NotificationInstance): void {
    if (notification.ariaLive === 'off') {
      return;
    }

    const text = [notification.title, notification.message].filter(Boolean).join(' ');
    this.liveAnnouncer.announce(text, notification.ariaLive);
  }

  /**
   * Returns the stack for a position, creating its overlay on first use. Stacks
   * are never torn down: they are a single empty element when idle, and keeping
   * them alive means a dismissed toast still has a host to animate out in.
   */
  private getStack(position: NotificationPosition): StackEntry {
    const existing = this.stacks.get(position);
    if (existing) {
      return existing;
    }

    const overlayRef = this.overlay.create({
      positionStrategy: this.buildPositionStrategy(position),
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      hasBackdrop: false,
      panelClass: ['du-toast-overlay-pane', `du-toast-overlay-pane--${position}`],
      disposeOnNavigation: false,
    });

    const portal = new ComponentPortal(
      NotificationContainerComponent,
      null,
      Injector.create({
        parent: this.injector,
        providers: [{ provide: NOTIFICATION_POSITION, useValue: position }],
      })
    );

    const componentRef = overlayRef.attach(portal);
    componentRef.instance.ariaLabel = this.defaults.containerAriaLabel;
    componentRef.instance.closeAriaLabel = this.defaults.closeAriaLabel;
    componentRef.instance.setTheme(this.theme);

    componentRef.instance.dismissed
      .pipe(takeUntil(this.destroyed))
      .subscribe((event) => this.dismiss(event.id, event.reason));

    componentRef.instance.actionInvoked
      .pipe(takeUntil(this.destroyed))
      .subscribe((notification) => this.refs.get(notification.id)?._emitAction(notification));

    const entry: StackEntry = { overlayRef, componentRef };
    this.stacks.set(position, entry);
    return entry;
  }

  private buildPositionStrategy(position: NotificationPosition): GlobalPositionStrategy {
    const strategy = this.overlay.position().global();
    const [vertical, horizontal] = position.split('-');

    if (vertical === 'top') {
      strategy.top('0');
    } else {
      strategy.bottom('0');
    }

    if (horizontal === 'left') {
      strategy.left('0');
    } else if (horizontal === 'right') {
      strategy.right('0');
    } else {
      strategy.centerHorizontally();
    }

    return strategy;
  }
}
